import { AnimatePresence, motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Tag } from '@/components';
import { I18nNamespace } from '@/lib/i18n-namespaces';

interface DirectorySidebarTagsProps {
    title: string;
    tags: string[];
    selectedTags: string[];
    onTagToggle: (tag: string) => void;
    onClear: () => void;
}

export function DirectorySidebarTags({
    title,
    tags,
    selectedTags,
    onTagToggle,
    onClear,
}: DirectorySidebarTagsProps) {
    const { t } = useTranslation(I18nNamespace.COMMON);

    const availableTags = tags.filter((tag) => !selectedTags.includes(tag));
    const hasSelected = selectedTags.length > 0;

    if (tags.length === 0 && !hasSelected) return null;

    return (
        <>
            <div
                className="w-full h-px shrink-0 self-stretch bg-grey-500 mt-6 mb-6"
                aria-hidden
            />
            <div className="flex max-h-[calc(100vh-260px)] w-full flex-col gap-4 overflow-y-auto px-2">
                <div className="flex items-center justify-between">
                    <span className="text-sm-medium text-white-100">
                        {title}
                    </span>
                    <AnimatePresence initial={false}>
                        {hasSelected && (
                            <motion.button
                                key="clear"
                                type="button"
                                onClick={onClear}
                                className="text-sm-medium text-grey-300 hover:text-white-100 transition-colors"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                transition={{ duration: 0.15 }}
                            >
                                {t('clear')}
                            </motion.button>
                        )}
                    </AnimatePresence>
                </div>
                <AnimatePresence initial={false}>
                    {hasSelected && (
                        <motion.div
                            key="selected-box"
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            exit={{ opacity: 0, height: 0 }}
                            transition={{ duration: 0.2, ease: 'easeInOut' }}
                            style={{ overflow: 'hidden' }}
                            className="flex flex-row flex-wrap gap-2 p-2 rounded border border-grey-500"
                        >
                            {selectedTags.map((tag) => (
                                <Tag
                                    key={tag}
                                    variant="base"
                                    onRemove={() => onTagToggle(tag)}
                                >
                                    {tag}
                                </Tag>
                            ))}
                        </motion.div>
                    )}
                </AnimatePresence>
                {availableTags.length > 0 && (
                    <motion.div
                        layout
                        transition={{ duration: 0.2, ease: 'easeInOut' }}
                        className="flex flex-row flex-wrap gap-2"
                    >
                        <AnimatePresence initial={false}>
                            {availableTags.map((tag) => (
                                <motion.div
                                    key={tag}
                                    layout
                                    initial={{ opacity: 0, scale: 0.9 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    exit={{ opacity: 0, scale: 0.9 }}
                                    transition={{ duration: 0.15 }}
                                >
                                    <Tag
                                        variant="add"
                                        onClick={() => onTagToggle(tag)}
                                    >
                                        {tag}
                                    </Tag>
                                </motion.div>
                            ))}
                        </AnimatePresence>
                    </motion.div>
                )}
            </div>
        </>
    );
}
